import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import LoginModal from "@/components/LoginModal";

const AuthContext = createContext(null);

// Bounded backoff for transient /auth/me failures (backend restart, 502/503/504,
// network drop). After the last retry we KEEP the current user — the backend
// stays authoritative and any later 401 clears the session state.
export const AUTH_RETRY_DELAYS_MS = [1000, 2000, 4000];

// 401 → the backend explicitly rejected the session.
// no HTTP response / 5xx → we could not reach a healthy backend; nothing is
// known about the session, so it must not be treated as a logout.
// any other 4xx (403 disabled/suspended, app mismatch…) → existing behaviour.
export const classifyAuthError = (error) => {
  const status = error?.response?.status;
  if (status == null || status >= 500) return "transient";
  if (status === 401) return "unauthenticated";
  return "rejected";
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authUnavailable, setAuthUnavailable] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const inFlightRef = useRef(null);

  const checkAuth = useCallback(() => {
    // Concurrent callers (AuthProvider mount + route guard mount, several
    // tabs' worth of guards) share one in-flight verification.
    if (inFlightRef.current) return inFlightRef.current;
    const run = (async () => {
      for (let attempt = 0; ; attempt += 1) {
        try {
          const res = await api.get("/auth/me");
          setUser(res.data);
          setAuthUnavailable(false);
          return;
        } catch (error) {
          const kind = classifyAuthError(error);
          if (kind !== "transient") {
            setUser(null);
            setAuthUnavailable(false);
            return;
          }
          if (attempt >= AUTH_RETRY_DELAYS_MS.length) {
            // Retries exhausted: preserve whatever we already know.
            setAuthUnavailable(true);
            return;
          }
          await sleep(AUTH_RETRY_DELAYS_MS[attempt]);
        }
      }
    })().finally(() => {
      inFlightRef.current = null;
      setLoading(false);
    });
    inFlightRef.current = run;
    return run;
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Single recovery re-check when connectivity or the tab comes back, only
  // while the last verification could not reach the backend. No polling.
  useEffect(() => {
    if (!authUnavailable) return undefined;
    const recover = () => {
      if (document.visibilityState === "hidden") return;
      checkAuth();
    };
    window.addEventListener("online", recover);
    document.addEventListener("visibilitychange", recover);
    return () => {
      window.removeEventListener("online", recover);
      document.removeEventListener("visibilitychange", recover);
    };
  }, [authUnavailable, checkAuth]);

  // Admin-console absolute lifetime. The deadline comes from the server
  // (`admin_session_expires_at` on /auth/me); the client never hardcodes it.
  // At the deadline we ask the server (checkAuth) — its 401
  // ADMIN_SESSION_EXPIRED clears the user; a transient failure keeps them.
  const adminExpiresAt = user?.admin_session_expires_at || null;
  useEffect(() => {
    if (!adminExpiresAt) return undefined;
    const deadline = Date.parse(adminExpiresAt);
    if (Number.isNaN(deadline)) return undefined;
    const delay = Math.min(Math.max(deadline - Date.now(), 0) + 250, 0x7fffffff);
    const timer = setTimeout(() => { checkAuth(); }, delay);
    // Timers stall while a tab sleeps; re-check against the server on wake.
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() >= deadline) checkAuth();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [adminExpiresAt, checkAuth]);

  // Any admin API answering 401 ADMIN_SESSION_EXPIRED ends the console session.
  useEffect(() => {
    const expire = () => { setUser(null); setAuthUnavailable(false); };
    window.addEventListener("advoset:admin-session-expired", expire);
    return () => window.removeEventListener("advoset:admin-session-expired", expire);
  }, []);

  const logout = async () => {
    let loginRoute = "/";
    try {
      const res = await api.post("/auth/logout");
      // Backend returns the registry-driven login page for the active
      // app so each PWA lands its user on its own sign-in surface.
      loginRoute = res?.data?.login_route || "/";
    } catch (error) {
      // Best-effort logout — network failure still clears the local user state below.
      console.warn("[auth] logout request failed; clearing local session anyway", error?.message || error);
    }
    setUser(null);
    window.location.href = loginRoute;
  };

  // Opens the login chooser (Google + phone OTP). Keeps the same API as before
  // so existing `login()` call-sites work unchanged.
  const login = () => setLoginOpen(true);

  const handleOtpLoggedIn = (payload) => {
    // OTP endpoint sets the session_token cookie (same as Google login); just refresh user.
    if (payload?.user) setUser(payload.user);
    setLoginOpen(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, authUnavailable, checkAuth, logout, login }}>
      {children}
      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onLoggedIn={handleOtpLoggedIn}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
